/**
 * useReceiptViewer Hook
 * 
 * Manages receipt viewing state and logic for the Sales component.
 */

import { useState, useEffect } from 'react';
import { getPaymentReceiptByPedidoId } from '../../../../../../../../crud/payments_api/payment_receipts';
import { VIEW_MODES, type ViewMode } from '../utils';

interface UseReceiptViewerProps {
  selectedPedido?: any;
  isPedidosMode: boolean;
  setViewMode: (mode: ViewMode) => void;
}

interface UseReceiptViewerReturn {
  currentReceipt: any;
  setCurrentReceipt: React.Dispatch<React.SetStateAction<any>>;
  loadingReceipt: boolean;
  setLoadingReceipt: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Custom hook to manage receipt viewing
 * Automatically loads receipt when a paid pedido is selected in pedidos mode
 */
export const useReceiptViewer = ({
  selectedPedido,
  isPedidosMode,
  setViewMode,
}: UseReceiptViewerProps): UseReceiptViewerReturn => {
  const [currentReceipt, setCurrentReceipt] = useState<any>(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // Auto-load receipt when paid pedido is selected
  useEffect(() => {
    if (isPedidosMode && selectedPedido?.payed) {
      setLoadingReceipt(true);
      getPaymentReceiptByPedidoId(selectedPedido.pedido_id)
        .then((response) => {
          // response.data is an array of receipts, extract the first one
          const receiptsArray = response.data;
          if (receiptsArray && Array.isArray(receiptsArray) && receiptsArray.length > 0) {
            setCurrentReceipt(receiptsArray[0]); // Use first receipt
            setViewMode(VIEW_MODES.RECEIPT); // Automatically switch to receipt view
          } else {
            console.error('No receipt found in response');
            setCurrentReceipt(null);
          }
        })
        .catch((error) => {
          console.error('Error loading receipt:', error);
          setCurrentReceipt(null);
        })
        .finally(() => {
          setLoadingReceipt(false);
        });
    }
  }, [selectedPedido, isPedidosMode, setViewMode]);

  return {
    currentReceipt,
    setCurrentReceipt,
    loadingReceipt,
    setLoadingReceipt,
  };
};
